import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function initGoogleAnalytics(measurementId: string): void {
  if (typeof window.gtag === "function") {
    // Already initialized — just configure this measurement ID
    window.gtag("config", measurementId, { send_page_view: false });
    return;
  }

  window.dataLayer = window.dataLayer || [];
  // gtag.js reads back each dataLayer entry expecting the native `arguments`
  // object, not a plain array. Pushing a spread array causes gtag.js to
  // silently ignore every command (no /g/collect hits are ever sent).
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };

  window.gtag("js", new Date());
  // Disable automatic page_view tracking; the SPA fires page_view manually on
  // route changes via useGoogleAnalyticsPageView.
  window.gtag("config", measurementId, { send_page_view: false });

  // Defer fetching the gtag.js script itself until the browser is idle (or a
  // short timeout elapses). All gtag() calls made in the meantime keep
  // queuing into dataLayer as usual and are processed once the script loads,
  // so no events are lost - this just keeps the ~175 KB script from
  // competing with the CSS/font/JS critical path for bandwidth on first load.
  const loadScript = () => {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
      measurementId,
    )}`;
    const firstScript = document.getElementsByTagName("script")[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      (document.head || document.body || document.documentElement).appendChild(
        script,
      );
    }
  };

  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(loadScript, { timeout: 2000 });
  } else {
    setTimeout(loadScript, 1000);
  }
}

interface GoogleAnalyticsProviderProps {
  measurementId: string;
  children: ReactNode;
}

export function GoogleAnalyticsProvider({
  measurementId,
  children,
}: GoogleAnalyticsProviderProps) {
  // Initialize synchronously during render so the window.gtag stub exists
  // before any child effect runs. React fires child effects before parent
  // effects, so initializing in a useEffect here would let the first
  // useGoogleAnalyticsPageView call fire while window.gtag is still undefined,
  // silently dropping the initial page_view. Guard on window for SSR/prerender.
  if (typeof window !== "undefined" && measurementId) {
    initGoogleAnalytics(measurementId);
  }

  return <>{children}</>;
}

export function useGoogleAnalyticsPageView() {
  const location = useLocation();

  useEffect(() => {
    window.gtag?.("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);
}
